import React, {useMemo} from 'react'
import clsx from 'clsx'
import {PageTabs, type PageTabsProps} from '../../../common/index.ts'
import {t} from '@colonist/ui'
import IconGameModeBase from '@static/images/game_mode_icons/base.svg'
import IconGameModeClassic1v1 from '@static/images/icon_gamemode_1v1.svg'
import IconGameModeCnK from '@static/images/game_mode_icons/cities_and_knights.svg'
import IconGameModeRush from '@static/images/game_mode_icons/colonist_rush.svg'
import styles from './RankedPageTabsContainer.module.scss'

export const RankedPageTabIds = {
    Classic4P: 'Classic4P',
    Classic1v1: 'Classic1v1',
    CitiesAndKnights4P: 'CitiesAndKnights4P',
    Rush4P: 'Rush4P',
} as const

type RankedPageTabId = typeof RankedPageTabIds[keyof typeof RankedPageTabIds]

export interface RankedPageTabsContainerProps {
    className?: string
    activeTabId: string
    enabledTabIds: RankedPageTabId[]
    onTabChange: (tabId: string) => void
    showRushTab?: boolean
    compactOnMediumScreensAndBelow?: boolean
    compactMode?: boolean
    switchToHorizontalOnMediumScreensAndBelow?: boolean
    direction?: PageTabsProps['direction']
}

const allRankedTabDefs = [
    {id: RankedPageTabIds.Classic4P, labelKey: 'strings:leaderboardPage.gameModes.baseGame', icon: IconGameModeBase},
    {id: RankedPageTabIds.Classic1v1, labelKey: 'strings:leaderboardPage.gameModes.1v1', icon: IconGameModeClassic1v1},
    {id: RankedPageTabIds.CitiesAndKnights4P, labelKey: 'strings:leaderboardPage.gameModes.citiesAndKnights', icon: IconGameModeCnK},
    {id: RankedPageTabIds.Rush4P, labelKey: 'strings:leaderboardPage.gameModes.rush', icon: IconGameModeRush},
] as const

export const RankedPageTabsContainer = ({
    className,
    activeTabId,
    enabledTabIds,
    onTabChange,
    showRushTab = false,
    compactMode = false,
    compactOnMediumScreensAndBelow = false,
    switchToHorizontalOnMediumScreensAndBelow = false,
    direction = 'vertical',
}: RankedPageTabsContainerProps) => {
    const tabs = useMemo(() => {
        const enabledTabIdSet = new Set(enabledTabIds)
        return allRankedTabDefs
            .filter((tab) => showRushTab || tab.id != RankedPageTabIds.Rush4P)
            .map((tab) => ({
                id: tab.id,
                label: t(tab.labelKey),
                icon: tab.icon,
                disabled: !enabledTabIdSet.has(tab.id as RankedPageTabId),
            }))
    }, [enabledTabIds, showRushTab])

    return (
        <PageTabs
            className={clsx(styles.rankedPageTabsContainer, className)}
            activeTabId={activeTabId}
            tabs={tabs}
            onTabChange={onTabChange}
            compactMode={compactMode}
            compactOnMediumScreensAndBelow={compactOnMediumScreensAndBelow}
            switchToHorizontalOnMediumScreensAndBelow={switchToHorizontalOnMediumScreensAndBelow}
            direction={direction}
        />
    )
}

